import { useMemo } from "react";
import { CalendarDays } from "@/components/icons";

/**
 * SelectDateTimePicker — native <select> dropdowns.
 * Value format: "YYYY-MM-DDTHH:mm" (24h internally, displayed in 12h).
 * Columns: Día · Hora · Min · AM/PM
 */
export default function SelectDateTimePicker({
  value,
  onChange,
  testid = "select-datetime-picker",
  busyRanges = [],
  daysAhead = 60,
  daysBehind = 7,
}) {
  const parsed = parseValue(value);

  // Build the list of selectable days
  const dayOptions = useMemo(() => {
    const list = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = -daysBehind; i <= daysAhead; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      list.push(d);
    }
    return list;
  }, [daysAhead, daysBehind]);

  const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
  const minuteOptions = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  // Defaults if no value
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentDay = parsed.day || today;
  const currentHour24 = parsed.hour != null ? parsed.hour : 9;
  const currentMin = parsed.minute != null ? nearestMinute(parsed.minute) : 0;

  const { hour12, period } = to12h(currentHour24);

  const dayKey = keyOf(currentDay);
  const emit = ({ day = currentDay, h12 = hour12, mi = currentMin, ap = period }) => {
    const h24 = to24h(h12, ap);
    const val = `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(
      day.getDate()
    )}T${pad(h24)}:${pad(mi)}`;
    onChange?.(val);
  };

  // Check busy for the current selection
  const isBusy = useMemo(() => {
    const start = new Date(currentDay);
    start.setHours(currentHour24, currentMin, 0, 0);
    return busyRanges.some((r) => start >= r.start && start < r.end);
  }, [currentDay, currentHour24, currentMin, busyRanges]);

  const summary = `${currentDay.toLocaleDateString("es-ES", {
    weekday: "short",
    day: "numeric",
    month: "short",
  })} · ${pad(hour12)}:${pad(currentMin)} ${period}`;

  const selectCls =
    "h-10 rounded-lg border border-black/[0.08] bg-white px-2.5 pr-7 text-[13px] font-medium text-black focus:outline-none focus:border-black/40 transition appearance-none cursor-pointer bg-no-repeat";

  const chevron = {
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='%23000' stroke-opacity='0.5' stroke-width='1.4' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>\")",
    backgroundPosition: "right 8px center",
    backgroundSize: "10px 6px",
  };

  return (
    <div
      className="rounded-lg border border-black/[0.08] bg-white p-2"
      data-testid={testid}
    >
      <div className="px-1 pt-1 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[12px] text-black/70">
          <CalendarDays className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
          <span className="capitalize font-medium">{summary}</span>
        </div>
        {isBusy && (
          <span className="text-[10px] font-semibold text-red-600 bg-red-50 rounded px-1.5 py-0.5">
            Ocupado
          </span>
        )}
      </div>

      <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-1.5">
        <select
          data-testid={`${testid}-day`}
          value={dayKey}
          onChange={(e) => {
            const [y, m, d] = e.target.value.split("-").map(Number);
            emit({ day: new Date(y, m - 1, d) });
          }}
          className={selectCls}
          style={chevron}
        >
          {dayOptions.map((d) => (
            <option key={keyOf(d)} value={keyOf(d)}>
              {formatDayLabel(d)}
            </option>
          ))}
        </select>
        <select
          data-testid={`${testid}-hour`}
          value={hour12}
          onChange={(e) => emit({ h12: parseInt(e.target.value) })}
          className={selectCls}
          style={chevron}
        >
          {hourOptions.map((h) => (
            <option key={h} value={h}>
              {pad(h)}
            </option>
          ))}
        </select>
        <select
          data-testid={`${testid}-minute`}
          value={currentMin}
          onChange={(e) => emit({ mi: parseInt(e.target.value) })}
          className={selectCls}
          style={chevron}
        >
          {minuteOptions.map((m) => (
            <option key={m} value={m}>
              {pad(m)}
            </option>
          ))}
        </select>
        <select
          data-testid={`${testid}-period`}
          value={period}
          onChange={(e) => emit({ ap: e.target.value })}
          className={selectCls}
          style={chevron}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
}

// ── Utils ───────────────────────────────────────────────────────────────
function pad(n) {
  return String(n).padStart(2, "0");
}

function keyOf(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function nearestMinute(m) {
  const opts = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
  return opts.reduce((best, cur) =>
    Math.abs(cur - m) < Math.abs(best - m) ? cur : best
  );
}

function to12h(h24) {
  const period = h24 >= 12 ? "PM" : "AM";
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return { hour12: h12, period };
}

function to24h(h12, period) {
  if (period === "AM") return h12 === 12 ? 0 : h12;
  return h12 === 12 ? 12 : h12 + 12;
}

function parseValue(v) {
  if (!v || typeof v !== "string")
    return { day: null, hour: null, minute: null };
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return { day: null, hour: null, minute: null };
  const day = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
  return {
    day,
    hour: parseInt(m[4]),
    minute: parseInt(m[5]),
  };
}

function formatDayLabel(d) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sameDay(d, today)) return "Hoy";
  if (sameDay(d, tomorrow)) return "Mañana";
  if (sameDay(d, yesterday)) return "Ayer";
  return d
    .toLocaleDateString("es-ES", {
      weekday: "short",
      day: "numeric",
      month: "short",
    })
    .replace(/\.$/g, "");
}
